import React, { Component } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import {
  Channel,
  Release,
  WriteableChannel,
} from "../../models";
import { NewChannel } from "./models";
import { ChannelRankDiffList } from "./ChannelRankDiffList";

interface Props {
  initialChannelList: Channel[];
  releases?: Release[];
  model: number;
  user: string;
  show: boolean;
  close: () => void;
  initialChannel?: Channel;
  onSave: (newChannel: WriteableChannel) => Promise<void> | void;
  computeNewChannelOrder: <T extends Pick<Channel, "id" | "stabilityRank">>(
    channelToEdit: T,
  ) => T[];
}

interface State {
  newChannel?: Partial<NewChannel>;
  modifiedChannels: NewChannel[];
}

export class AddEditChannelModal extends Component<Props, State> {
  constructor(props: Readonly<Props> | Props) {
    super(props);
    this.state = {
      modifiedChannels: [],
    };
  }

  async componentDidMount(): Promise<void> { }

  render() {
    return (
      <Modal show={this.props.show} onHide={() => this.close()}>
        <Modal.Header closeButton>
          {this.props.initialChannel ? "Edit" : "Create"} Channel
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Row>
              <Col xs={12}>
                <Form.Group controlId="channelName">
                  <Form.Label>Channel Name</Form.Label>

                  <Form.Control
                    type="text"
                    placeholder="Name"
                    disabled={!!this.props.initialChannel?.id?.name}
                    value={this.getName()?.replaceAll("_-_", "/")}
                    onChange={(e) =>
                      this.setChannelAndModifiedList({
                        release: this.getRelease(),
                        stabilityRank: this.getStabilityRank(),
                        id: {
                          model: this.getModel(),
                          name: e.target.value.replaceAll("/", "_-_"),
                        },
                      })
                    }
                  />
                </Form.Group>
              </Col>

              <Col xs={6}>
                <Form.Group controlId="release">
                  <Form.Label>Release</Form.Label>

                  <Form.Select
                    onChange={(e) =>
                      this.setChannelAndModifiedList({
                        release: e.target.value,
                        stabilityRank: this.getStabilityRank(),
                        id: {
                          model: this.getModel(),
                          name: this.getName(),
                        },
                      })
                    }
                    value={this.getRelease()}
                  >
                    <option value="">-- select release --</option>
                    {this.props.releases?.map((release) => (
                      <option
                        key={`release-select-${release.id}`}
                        value={release._links.self.href}
                      >
                        {release.version}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col xs={6}>
                <Form.Group controlId="stabilityRank">
                  <Form.Label>Stability Rank</Form.Label>

                  <Form.Control
                    type="number"
                    min={1}
                    max={this.getMaxRank()}
                    value={this.getStabilityRank() || ""}
                    onChange={(e) => {
                      const newRank: number | null =
                        e.target.value.trim().length !== 0
                          ? Number(e.target.value)
                          : null;

                      this.setChannelAndModifiedList({
                        release: this.getRelease(),
                        stabilityRank: newRank,
                        id: {
                          model: this.getModel(),
                          name: this.getName(),
                        },
                      });
                    }}
                  />
                  {this.state.modifiedChannels.length !== 0 && (
                    <ChannelRankDiffList
                      initialChannelList={this.props.initialChannelList}
                      modifiedChannels={this.state.modifiedChannels}
                    />
                  )}
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => this.close()}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type={"submit"}
            disabled={!this.getName() || !this.getRelease()}
            onClick={async () => {
              await this.props.onSave({
                id: { name: this.getName()!, model: this.getModel() },
                lastUpdatedTime: new Date().toISOString(),
                lastUpdatedBy: this.props.user,
                release: this.getRelease()!,
                stabilityRank: this.getStabilityRank() || null,
              });
              this.close();
            }}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  private close() {
    this.setState({
      newChannel: undefined,
      modifiedChannels: [],
    });
    this.props.close();
  }

  private getName(): string {
    return (
      this.state.newChannel?.id?.name ||
      this.props.initialChannel?.id?.name ||
      ""
    );
  }

  private getModel(): number {
    return (
      this.props.model
    );
  }

  private getRelease(): string {
    return (
      this.state.newChannel?.release ||
      this.props.initialChannel?.release?._links.self.href ||
      ""
    );
  }

  private getStabilityRank(): number | null {
    if (this.state.newChannel) {
      return this.state.newChannel.stabilityRank || null;
    }
    return this.props.initialChannel?.stabilityRank || null;
  }

  private getMaxRank(): number {
    const initiallyRankedChannels = this.props.initialChannelList.filter(
      (channel) => channel.stabilityRank !== null,
    );
    return (
      initiallyRankedChannels.length +
      (!!this.props.initialChannel?.stabilityRank ? 0 : 1)
    );
  }

  private setChannelAndModifiedList(newChannel: Partial<NewChannel>) {
    const startingRank = this.props.initialChannel?.stabilityRank || null;
    this.setState({
      newChannel: newChannel,
      modifiedChannels:
        newChannel.stabilityRank !== startingRank
          ? this.props.computeNewChannelOrder({
            ...newChannel,
            id: {
              name: newChannel?.id?.name || "Unnamed",
              model: newChannel?.id?.model || 1,
            },
            stabilityRank: newChannel.stabilityRank || null,
          })
          : [],
    });
  }
}
